import { useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils/general";
import { X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { validateGameDescription } from "../../lib/utils/textValidation";
import { useToast } from "@/hooks/useToast";
import { ASCII_FACES } from "@/lib/constants/ascii";

interface ImmersiveTextInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onClose: () => void;
  placeholder?: string;
}

export function ImmersiveTextInput({
  value,
  onChange,
  onSubmit,
  onClose,
  placeholder = "Type your game idea..."
}: ImmersiveTextInputProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [currentFace, setCurrentFace] = useState(ASCII_FACES[0]);
  const [isPasting, setIsPasting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Trigger enter animation after mount
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Effect for cycling through ASCII faces during loading
  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      setCurrentFace(prev => {
        const currentIndex = ASCII_FACES.indexOf(prev);
        const nextIndex = (currentIndex + 1) % ASCII_FACES.length;
        return ASCII_FACES[nextIndex];
      });
    }, 150);

    return () => clearInterval(interval);
  }, [isLoading]);

  const calculateFontSize = (length: number): string => {
    if (length === 0) return "text-5xl";
    if (length < 50) return "text-5xl";
    if (length < 100) return "text-4xl";
    if (length < 225) return "text-3xl";
    return "text-2xl";
  };

  const handleSubmit = async () => {
    const validation = validateGameDescription(value);
    if (!validation.isValid) {
      const errorMessage = validation.error || 'Invalid input';
      setValidationError(errorMessage);
      toast({
        title: "Invalid Input",
        description: errorMessage,
        variant: "destructive",
      });
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    setIsLoading(true);
    try {
      await onSubmit(value);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      await handleSubmit();
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    // Wait for exit animation to complete before calling onClose
    setTimeout(onClose, 300);
  };

  const simulateTyping = async (pastedText: string, currentValue: string) => {
    setIsPasting(true);
    const baseDelay = 35; // Base delay between characters in ms
    let currentText = currentValue;
    let formattedText = '';
    
    for (let i = 0; i < pastedText.length; i++) {
      // Random variation in typing speed
      const randomDelay = baseDelay + Math.random() * 10;
      
      await new Promise(resolve => setTimeout(resolve, randomDelay));
      
      // Check for sentence ending pattern: period + space + capital letter
      const isEndOfSentence = i < pastedText.length - 2 && 
        pastedText[i] === '.' && 
        pastedText[i + 1] === ' ' && 
        /[A-Z]/.test(pastedText[i + 2]);

      // Add character with natural typing variation
      currentText += pastedText[i];
      formattedText += pastedText[i];
      
      // If we found a sentence end, add a line break after the period
      if (isEndOfSentence) {
        currentText += '\n\n';
        formattedText += '\n\n';
        i++; // Skip the space since we're adding line breaks
      }
      
      onChange(currentText);
      
      // Add slightly longer pause after punctuation or line breaks
      if (/[.,!?]/.test(pastedText[i]) || isEndOfSentence) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    setIsPasting(false);
    return formattedText; // Return the formatted text with line breaks
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (isPasting) return; // Prevent multiple paste operations

    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const cursorPosition = e.currentTarget.selectionStart || 0;
    
    // Split current value into before and after cursor
    const beforeCursor = value.substring(0, cursorPosition);
    const afterCursor = value.substring(cursorPosition);
    
    // Simulate typing the pasted text and use the formatted version
    simulateTyping(pastedText, beforeCursor).then((formattedText) => {
      // After typing animation, combine all parts with the formatted text
      onChange(beforeCursor + formattedText + afterCursor);
    });
  };

  if (isLoading) {
    return (
      <div className={cn(
        "fixed inset-x-0 bottom-0 top-0 z-40 flex flex-col items-center justify-center bg-stone-900",
        "transition-all duration-500 ease-in-out",
        isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
      )}>
        <div className="text-4xl font-instrument text-ascii animate-pulse-soft mb-8">
          {currentFace}
        </div>
        <p className="text-white/50 font-instrument text-2xl">
          Generating your game...
        </p>
      </div>
    );
  }

  return (
    <div className={cn(
      "fixed inset-x-0 bottom-0 -top-20 z-40 flex items-center justify-center bg-stone-900",
      "transition-all duration-300 ease-in-out transform",
      isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
    )}>
      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10",
          "transition-all duration-300 ease-in-out transform",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
        )}
        onClick={handleClose}
      >
        <X className="h-6 w-6" />
      </Button>

      {/* Text input area */}
      <div className="relative w-full h-full flex flex-col items-center justify-center">
        <div className={cn(
          "w-full max-w-4xl px-8 h-[70vh] flex items-center justify-center",
          "transition-all duration-300 ease-in-out transform",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        )}>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => {
              if (!isPasting) {
                onChange(e.target.value);
                setValidationError(null);
              }
            }}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(
              "w-full bg-transparent text-center text-white placeholder:text-white/50",
              "border-none outline-none resize-none",
              "transition-all duration-300 font-instrument leading-normal",
              "whitespace-pre-wrap break-words",
              "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
              calculateFontSize(value.length),
              isShaking && "animate-shake",
              isPasting && "cursor-wait"
            )}
            style={{
              height: "100%",
              maxHeight: "100%",
              overflowY: "auto",
              padding: "2rem"
            }}
          />
        </div>

        {/* Go button */}
        <div className={cn(
          "absolute bottom-8 left-0 right-0 flex justify-center",
          "transition-all duration-300 ease-in-out transform",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        )}>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || isPasting}
            className={cn(
              "bg-logo/70 hover:bg-white/20 text-background rounded-full px-8 py-6",
              "font-instrument text-xl transition-all duration-300",
              "flex items-center gap-2 hover:gap-3", // Gap increases on hover
              (isLoading || isPasting) && "opacity-50 cursor-not-allowed"
            )}
          >
            Go
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Validation error */}
        {validationError && (
          <div className={cn(
            "absolute bottom-28 left-0 right-0 text-center", // Moved up to make room for the button
            "transition-all duration-300 ease-in-out transform",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}>
            <p className="text-red-400 text-sm bg-black/20 backdrop-blur-sm py-2 px-4 rounded-full inline-block font-sans">
              {validationError}
            </p>
          </div>
        )}

        {/* Hint text */}
        {value.length === 0 && (
          <div className={cn(
            "absolute bottom-28 left-0 right-0 text-center", // Moved up to make room for the button
            "transition-all duration-300 ease-in-out transform",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}>
            <p className="text-muted-foreground text-xs font-sans">
              Press Enter to submit • Esc to close
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 